import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth, ROLE_LABEL } from "@/lib/auth";
import { Card, Button, Input, Select, Modal, PageHeader, EmptyState, Badge } from "@/components/kit";
import { TID } from "@/constants/testIds";
import { Plus, Trash } from "@phosphor-icons/react";

const roleBadgeTone = { super_admin: "success", branch_admin: "info", agent: "warning", staff: "neutral" };

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ role: "staff" });
  const [err, setErr] = useState("");

  const load = async () => {
    const [u, b] = await Promise.all([api.get("/users"), api.get("/branches")]);
    setUsers(u.data); setBranches(b.data);
  };
  useEffect(() => { load(); }, []);

  const availableRoles = user.role === "super_admin"
    ? ["super_admin", "branch_admin", "agent", "staff"]
    : ["agent", "staff"];

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    const body = { ...form };
    if (user.role === "branch_admin") body.branch_id = user.branch_id;
    if (!body.branch_id) delete body.branch_id;
    try {
      await api.post("/users", body);
      setOpen(false); setForm({ role: availableRoles[availableRoles.length - 1] });
      load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    await api.delete(`/users/${id}`);
    load();
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 pb-24 md:pb-8 fade-up">
      <PageHeader
        title="Users"
        subtitle="Manage staff members and their role assignments."
        action={
          <Button onClick={() => setOpen(true)} data-testid={TID.addBtn("user")}>
            <Plus size={16} weight="bold" /> Add user
          </Button>
        }
      />
      <Card>
        {users.length === 0 ? (
          <EmptyState title="No users" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Role</th>
                  <th className="text-left px-4 py-3">Branch</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3 font-medium">{u.name}</td>
                    <td className="px-4 py-3 mono">{u.email}</td>
                    <td className="px-4 py-3"><Badge tone={roleBadgeTone[u.role]}>{ROLE_LABEL[u.role]}</Badge></td>
                    <td className="px-4 py-3 text-slate-600">
                      {branches.find((b) => b.id === u.branch_id)?.name || "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {user.role === "super_admin" && u.id !== user.id && (
                        <button onClick={() => del(u.id)} data-testid={`del-user-${u.id}`} className="text-rose-600 hover:text-rose-800">
                          <Trash size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title="Add user">
        <form onSubmit={submit} className="space-y-4">
          <Input label="Full name" required testId="user-name" value={form.name || ""} onChange={e=>setForm({...form,name:e.target.value})} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Email" type="email" required testId="user-email" value={form.email || ""} onChange={e=>setForm({...form,email:e.target.value})} />
            <Input label="Password" type="password" required testId="user-password" value={form.password || ""} onChange={e=>setForm({...form,password:e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select label="Role" required testId="user-role" value={form.role} onChange={e=>setForm({...form,role:e.target.value})}>
              {availableRoles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </Select>
            {user.role === "super_admin" && (
              <Select label="Branch" testId="user-branch" value={form.branch_id || ""} onChange={e=>setForm({...form,branch_id:e.target.value})}>
                <option value="">— none (super admin) —</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </Select>
            )}
          </div>
          <Input label="Phone (optional)" testId="user-phone" value={form.phone || ""} onChange={e=>setForm({...form,phone:e.target.value})} />
          {err && <div className="text-sm text-rose-600">{String(err)}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid={TID.saveBtn}>Create</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
